import { Link, useParams } from 'react-router-dom'
import { Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks'
import { Permissions, canEditPromptPhase } from '@/lib/permissions'
import type { Prompt } from '@/types'
import { PhaseBadge } from './PhaseBadge'

interface PromptRowProps {
  prompt: Prompt
  onDelete: (prompt: Prompt) => void
}

export function PromptRow({ prompt, onDelete }: PromptRowProps) {
  const { projectId } = useParams()
  const { hasPermission, getProjectRoles } = useAuth(projectId)
  const projectRoles = projectId ? getProjectRoles(projectId) : []
  const phaseAllowsEdit = canEditPromptPhase(prompt.projectPhaseName, projectRoles)
  // The interactive-mode prompt is required by the platform: editable, but not deletable.
  const isReserved = prompt.internalName === 'interactive-mode'
  const canEdit = phaseAllowsEdit && hasPermission(Permissions.Project.Prompt.Update, projectId)
  const canDelete = !isReserved && phaseAllowsEdit && hasPermission(Permissions.Project.Prompt.Delete, projectId)
  const promptPath = `/projects/${projectId}/workflows/prompts/${prompt.id}`

  return (
    <div className="relative rounded-2xl border border-border bg-card hover:bg-muted/30" data-testid="prompt-row">
      <div className="flex items-center justify-between px-4 py-2.5 gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 py-1">
            <Link
              to={promptPath}
              className="text-base font-bold leading-[150%] tracking-[0.015em] text-foreground truncate after:absolute after:inset-0 after:rounded-2xl"
              data-testid="prompt-row-link"
            >
              {prompt.name}
            </Link>
            <PhaseBadge name={prompt.projectPhaseName} color={prompt.projectPhaseDisplayColor} />
            {prompt.isSeeded && (
              <span
                className="shrink-0 inline-flex items-center h-6 px-2.5 rounded-full border text-xs font-medium"
                style={{
                  borderColor: '#767C96',
                  color: '#767C96',
                  backgroundColor: 'rgba(118, 124, 150, 0.16)',
                }}
              >
                System
              </span>
            )}
          </div>
          {prompt.description && (
            <p className="text-sm text-muted-foreground mt-1 truncate">{prompt.description}</p>
          )}
        </div>
        {(canEdit || canDelete) && (
          <div className="relative z-10 flex items-center gap-1 shrink-0">
            {canEdit && (
              <Button type="button" variant="ghost" size="iconSm" asChild>
                <Link to={promptPath} aria-label={`Edit ${prompt.name}`}>
                  <Pencil className="h-4 w-4" />
                </Link>
              </Button>
            )}
            {canDelete && (
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                aria-label={`Delete ${prompt.name}`}
                onClick={() => onDelete(prompt)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
